import { useCallback, useEffect, useMemo, useState } from "react";
import * as mqtt from "precompiled-mqtt";
import { IMQTTBrokerSettings } from "contexts/HMICachedDataContext/HMICachedDataContextInterfaces";
import { CHECK_LOCALSERVER_CONNECTIVITY_INTERVAL, KEEP_ALIVE_TOPIC, MQTTEventsEnum } from "constants/constants";

const { connect } = mqtt;

const MIN_RATE_ACTIVE = Math.max(CHECK_LOCALSERVER_CONNECTIVITY_INTERVAL, 5000);
const MIN_RATE_INACTIVE = Math.max(CHECK_LOCALSERVER_CONNECTIVITY_INTERVAL / 5, 5000);

function useMQTTKeepAlive(dedicatedLocalserver: boolean, options?: IMQTTBrokerSettings, code?: string) {
    const [isLocalServerActive, setIsLocalServerActive] = useState(false);
    const [isBrokerConnected, setIsBrokerConnected] = useState(false);

    const keepAliveInterval = useMemo(
        () => (!isBrokerConnected || !isLocalServerActive ? MIN_RATE_INACTIVE : MIN_RATE_ACTIVE),
        [isBrokerConnected, isLocalServerActive],
    );

    const checkMQTT = useCallback((options: IMQTTBrokerSettings, code: string) => {
        return new Promise((resolve, reject) => {
            try {
                const mosquittoUrl = options.host;
                let connectionHandled = false;
                const isWSS = mosquittoUrl.startsWith("wss");
                const client = connect(mosquittoUrl, {
                    username: options.user,
                    password: options.password,
                    protocol: isWSS ? "wss" : "ws",
                    clean: true,
                    clientId: code,
                });

                const handleError = (err: Error) => {
                    connectionHandled = true;
                    setIsBrokerConnected(false);
                    setIsLocalServerActive(false);
                    reject(err);
                };

                const handleConnect = () => {
                    if (connectionHandled) {
                        return;
                    }
                    connectionHandled = true;
                    setIsBrokerConnected(true);
                    client.subscribe({ [KEEP_ALIVE_TOPIC]: { qos: 1 } }, (err) => {
                        if (err) {
                            client.end();
                            reject(new Error(`Failed to subscribe to ${KEEP_ALIVE_TOPIC}`));
                            return;
                        }
                    });
                };

                const handleMessage = (topic: string, message: Buffer) => {
                    connectionHandled = true;
                    if (topic === KEEP_ALIVE_TOPIC) {
                        try {
                            const response = JSON.parse(message.toString()) as {
                                lastCloudSync: Date;
                                serverTime: number;
                            };

                            if (response.serverTime) {
                                const currentClientTime = Date.now();
                                const timeDifference = Math.abs(currentClientTime - response.serverTime);
                                const isTimeInSync = timeDifference < 60000;

                                if (isTimeInSync) {
                                    client.end();
                                    setIsLocalServerActive(true);
                                    resolve(response);
                                } else {
                                    client.end();
                                    setIsLocalServerActive(false);
                                    reject(new Error("Server time is out of sync by more than 1 minute"));
                                }
                            } else {
                                client.end();
                                setIsLocalServerActive(false);
                                reject(new Error("Server response without timestamp"));
                            }
                        } catch (error) {
                            client.end();
                            setIsLocalServerActive(false);
                            reject(new Error("Failed to parse response"));
                        }
                    }
                };

                const handleClose = () => {
                    if (!connectionHandled) {
                        connectionHandled = true;
                        setIsBrokerConnected(false);
                        setIsLocalServerActive(false);
                        reject(new Error("Connection closed before being established"));
                    }
                };

                client.on(MQTTEventsEnum.ERROR, handleError);

                client.on(MQTTEventsEnum.CONNECT, handleConnect);

                client.on(MQTTEventsEnum.MESSAGE, handleMessage);

                client.on(MQTTEventsEnum.CLOSE, handleClose);
            } catch (error) {
                setIsBrokerConnected(false);
                setIsLocalServerActive(false);
            }
        });
    }, []);

    useEffect(() => {
        if (options && code && dedicatedLocalserver) {
            checkMQTT(options, code);

            const timerId = setInterval(async () => {
                await checkMQTT(options, code);
            }, keepAliveInterval);

            return () => clearInterval(timerId);
        } else {
            setIsBrokerConnected(!dedicatedLocalserver);
            setIsLocalServerActive(!dedicatedLocalserver);
        }
    }, [options, code, keepAliveInterval, checkMQTT, dedicatedLocalserver]);

    return { isLocalServerActive, isBrokerConnected };
}

export default useMQTTKeepAlive;
